import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  BooleanField,
  EditButton,
  Edit,
  SimpleForm,
  TextInput,
  NumberInput,
  BooleanInput,
  required,
  Show,
  SimpleShowLayout,
  TopToolbar,
  FunctionField,
  useRecordContext,
} from 'react-admin';
import { Box, Typography, Chip, Alert, Grid } from '@mui/material';

// 회원등급 색상 매핑
const LEVEL_COLORS: Record<string, string> = {
  BRONZE: '#CD7F32',
  SILVER: '#C0C0C0',
  GOLD: '#FFD700',
  PLATINUM: '#E5E4E2',
  VIP: '#7A68A7',
};

// 등급 라벨 표시 컴포넌트
const LevelChip = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Chip
      label={record.name}
      sx={{
        backgroundColor: LEVEL_COLORS[record.level] || '#ccc',
        color: ['SILVER', 'PLATINUM'].includes(record.level) ? '#333' : '#fff',
        fontWeight: 'bold',
      }}
    />
  );
};

// 회원등급 목록
export const MembershipConfigList = () => (
  <>
    <Box sx={{ mb: 3, mt: 2 }}>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#1C1C1E' }}>
        회원등급 관리
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
        회원등급별 혜택을 설정합니다. 등급 기준 금액과 적립률, 쿠폰 혜택 등을 관리할 수 있습니다.
      </Typography>
    </Box>
    <List
      title=" "
      actions={<TopToolbar />}
      perPage={10}
      sort={{ field: 'displayOrder', order: 'ASC' }}
      exporter={false}
    >
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <FunctionField label="등급" render={() => <LevelChip />} />
        <TextField source="level" label="등급 코드" />
        <FunctionField
          label="기준 금액"
          render={(record: any) => (
            <Typography variant="body2">
              {record.minPurchaseAmount?.toLocaleString()}원
              {record.maxPurchaseAmount
                ? ` ~ ${record.maxPurchaseAmount.toLocaleString()}원`
                : ' 이상'}
            </Typography>
          )}
        />
        <FunctionField
          label="적립률"
          render={(record: any) => `${record.pointRate}%`}
        />
        <FunctionField
          label="무료배송 쿠폰"
          render={(record: any) => `월 ${record.freeShippingCouponCount}장`}
        />
        <FunctionField
          label="할인쿠폰"
          render={(record: any) =>
            `${record.discountCouponPercent}% / 월 ${record.discountCouponCount}장`
          }
        />
        <BooleanField source="isActive" label="활성" />
        <EditButton />
      </Datagrid>
    </List>
  </>
);

// 회원등급 수정
export const MembershipConfigEdit = () => (
  <Edit title="회원등급 수정">
    <SimpleForm>
      <Alert severity="info" sx={{ mb: 3, width: '100%' }}>
        회원등급 코드(BRONZE, SILVER 등)는 시스템 연동을 위해 수정할 수 없습니다.
        혜택 설정만 변경 가능합니다.
      </Alert>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextInput source="level" label="등급 코드" disabled fullWidth />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInput source="name" label="등급명" validate={required()} fullWidth />
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>등급 기준</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <NumberInput
            source="minPurchaseAmount"
            label="최소 구매금액 (원)"
            validate={required()}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <NumberInput
            source="maxPurchaseAmount"
            label="최대 구매금액 (원)"
            helperText="비워두면 무제한"
            fullWidth
          />
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>적립 혜택</Typography>
      <NumberInput
        source="pointRate"
        label="적립률 (%)"
        validate={required()}
        step={0.1}
        fullWidth
      />

      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>쿠폰 혜택 (월간)</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <NumberInput
            source="freeShippingCouponCount"
            label="무료배송 쿠폰 (장)"
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <NumberInput
            source="discountCouponCount"
            label="할인쿠폰 (장)"
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <NumberInput
            source="discountCouponPercent"
            label="할인쿠폰 할인율 (%)"
            fullWidth
          />
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>기타 설정</Typography>
      <TextInput
        source="description"
        label="설명"
        multiline
        rows={2}
        fullWidth
      />
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <NumberInput
            source="displayOrder"
            label="정렬 순서"
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <BooleanInput
            source="isActive"
            label="활성화"
          />
        </Grid>
      </Grid>
    </SimpleForm>
  </Edit>
);

// 회원등급 상세보기
export const MembershipConfigShow = () => (
  <Show title="회원등급 상세">
    <SimpleShowLayout>
      <TextField source="level" label="등급 코드" />
      <TextField source="name" label="등급명" />
      <NumberField source="minPurchaseAmount" label="최소 구매금액" options={{ style: 'currency', currency: 'KRW' }} />
      <NumberField source="maxPurchaseAmount" label="최대 구매금액" options={{ style: 'currency', currency: 'KRW' }} />
      <FunctionField label="적립률" render={(record: any) => `${record.pointRate}%`} />
      <NumberField source="freeShippingCouponCount" label="무료배송 쿠폰 (월)" />
      <NumberField source="discountCouponCount" label="할인쿠폰 (월)" />
      <NumberField source="discountCouponPercent" label="할인쿠폰 할인율 (%)" />
      <TextField source="description" label="설명" />
      <BooleanField source="isActive" label="활성화" />
    </SimpleShowLayout>
  </Show>
);
